import React from 'react';
import { ArrowRight, Maximize2, Stamp, FileEdit, ArrowUpRight } from 'lucide-react';
import { AskAiSection, ECOSYSTEM_LOGO_SLOT_CLASS, buildAskAiPrompt, buildAskAiProviders, ecosystemLogoClass } from '@rehub/ask-ai';
import { LANDING_HEADLINE, LANDING_SUBTEXT, LANDING_CTA, LANDING_FEATURES } from '../constants';

const iconMap: Record<string, React.FC<{ size?: number; className?: string }>> = {
  Maximize2,
  Stamp,
  FileEdit,
};

type RehubApp = { name: string; tagline: string; description: string; url: string; logo: string; current?: boolean };

const REHUB_APPS: RehubApp[] = [
  { name: 'PhotoEdit', tagline: 'AI Photo Editing', description: 'Professional AI photo editing — tone, colour, detail and crop for every listing image.', url: 'https://photoedit.rehub.studio', logo: 'https://photoedit.rehub.studio/photoedit-logo.png' },
  { name: 'HDR Photo', tagline: 'AI HDR Merging', description: 'Merge bracketed exposures into studio-quality HDR, then enhance automatically.', url: 'https://hdrphoto.rehub.studio', logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png' },
  { name: 'Photoclear.ai', tagline: 'AI Decluttering', description: 'Remove clutter and distractions instantly with AI to reveal clean, buyer-ready rooms.', url: 'https://photoclear.rehub.studio', logo: 'https://photoclear.rehub.studio/Photoclear Logo HR Trans.png' },
  { name: 'Virtual Staging', tagline: 'AI Virtual Staging', description: 'Transform empty rooms into beautifully furnished interiors in minutes.', url: 'https://staging.rehub.studio', logo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png' },
  { name: 'ImageMotion', tagline: 'AI Motion', description: 'Turn static listing photos into cinematic property videos automatically.', url: 'https://imagemotion.rehub.studio', logo: 'https://imagemotion.rehub.studio/Logo.webp' },
  { name: 'VideoMotion', tagline: 'AI Video Editing', description: 'Turn raw walkthrough footage into polished, social-ready video edits.', url: 'https://videomotion.rehub.studio', logo: 'https://videomotion.rehub.studio/logo.png' },
  { name: 'PropertyCuts', tagline: 'Video Assembler', description: 'Stitch photos, clips and floor plans into a finished property video.', url: 'https://propertycuts.rehub.studio', logo: 'https://propertycuts.rehub.studio/logo.png' },
  { name: 'Hyper Dusk', tagline: 'AI Day-to-Dusk', description: 'Transform daytime exteriors into stunning dusk shots with AI-powered lighting.', url: 'https://hyperdusk.rehub.studio', logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png' },
  { name: 'Screen Record', tagline: 'Guided Recordings', description: 'Record guided 360° tours and screen walkthroughs with voice, camera and a shareable link.', url: 'https://record.rehub.studio', logo: '/screenrecord-logo.png' },
  { name: '360 Tours', tagline: 'Virtual Tours', description: 'Build immersive virtual tours for property listings and developments.', url: 'https://360tours.rehub.studio', logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png' },
  { name: 'Planos', tagline: 'Interactive Floor Plans', description: 'Create interactive floor plans that let buyers explore properties visually.', url: 'https://planos.rehub.studio', logo: 'https://planos.rehub.studio/Planos Logo Red.png' },
  { name: 'Sketchplan', tagline: 'AI Floor Plans', description: 'Turn rough sketches into accurate, professional floor plans.', url: 'https://sketchplan.rehub.studio', logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp' },
];

const ASK_AI_PRODUCT = 'BatchTools';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://tools.rehub.studio',
  extraFocus:
    'Focus on the free browser batch resize, watermark and rename utility for property photos. It does not charge credits for those tools.',
});

const EcosystemGrid: React.FC = () => (
  <div className="max-w-6xl mx-auto">
    <div className="text-center mb-12">
      <p className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4">The ReHub Ecosystem</p>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">One platform. Every property marketing app.</h2>
      <p className="text-slate-400 max-w-2xl mx-auto">Each app is purpose-built for property and works beautifully on its own — or together as one seamless workflow.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {REHUB_APPS.map((app) => (
        <a
          key={app.name}
          href={app.url}
          target={app.current ? undefined : '_blank'}
          rel={app.current ? undefined : 'noopener noreferrer'}
          className="group flex flex-col rounded-2xl border border-slate-700/70 bg-slate-900/50 backdrop-blur-sm p-6 text-left transition-all hover:border-slate-500 hover:bg-slate-900/80"
        >
          <div className={`mb-5 ${ECOSYSTEM_LOGO_SLOT_CLASS}`}>
            <img src={app.logo} alt={app.name} className={ecosystemLogoClass(app.name)} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{app.tagline}</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 grow">{app.description}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
            {app.current ? "You're here" : 'Launch app'}
            {!app.current && <ArrowUpRight size={16} className="text-slate-500 group-hover:text-teal-300 transition-colors" />}
          </span>
        </a>
      ))}
    </div>
  </div>
);

interface LandingPageProps {
  onStart: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onStart }) => {
  return (
    <div className="flex-1 flex flex-col">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative container mx-auto px-4 py-20 sm:py-28 md:py-36 text-center max-w-4xl">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="BatchTools" className="h-20 sm:h-28 w-auto object-contain" />
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-900/30 border border-cyan-500/30 rounded-full text-cyan-300 text-xs font-medium mb-8">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
            Free &middot; No credit card required
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight mb-6 tracking-tight">
            {LANDING_HEADLINE}
          </h1>

          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {LANDING_SUBTEXT}
          </p>

          <button
            onClick={onStart}
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] touch-manipulation"
          >
            {LANDING_CTA}
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Feature Blocks */}
      <section className="container mx-auto px-4 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {LANDING_FEATURES.map((feature) => {
            const Icon = iconMap[feature.icon] || Maximize2;
            return (
              <div
                key={feature.title}
                className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 sm:p-8 hover:border-cyan-500/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-900/30 border border-cyan-500/20 flex items-center justify-center mb-5 group-hover:bg-cyan-900/50 transition-colors">
                  <Icon size={22} className="text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="border-t border-slate-800 bg-slate-950/50">
        <div className="container mx-auto px-4 py-16 sm:py-20 max-w-6xl">
          <EcosystemGrid />
        </div>
      </section>
      <AskAiSection
        productName={ASK_AI_PRODUCT}
        prompt={ASK_AI_PROMPT}
        providers={ASK_AI_PROVIDERS}
        description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about BatchTools’ free resize, watermark and rename tools for property photos."
      />
    </div>
  );
};

export default LandingPage;
